import { useEffect, useRef, useState } from "react";
import { Copy, Check, Download, Image as ImageIcon, Loader2, Play, Plus, Sparkles, Trash2, Upload, Type, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  createShareText,
  deleteShareMedia,
  deleteShareText,
  downloadShareMediaCleaned,
  downloadShareMediaOriginal,
  listShareMedia,
  listShareText,
  shareMediaThumbUrl,
  updateShareText,
  uploadShareMedia,
  type ShareMedia,
  type ShareText,
} from "../api/share";
import { formatDateTime } from "../utils/format";

type Tab = "media" | "text";

export function ShareCenterPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("media");

  return (
    <div className="p-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold">{t("shareCenter.title")}</h1>
        <p className="mt-1 text-ink-500 text-sm">{t("shareCenter.subtitle")}</p>
      </header>

      <div className="mb-6 flex gap-1 border-b border-ink-700">
        <TabButton active={tab === "media"} onClick={() => setTab("media")} icon={<ImageIcon className="size-4" />}>
          {t("shareCenter.tabs.media")}
        </TabButton>
        <TabButton active={tab === "text"} onClick={() => setTab("text")} icon={<Type className="size-4" />}>
          {t("shareCenter.tabs.text")}
        </TabButton>
      </div>

      {tab === "media" ? <MediaTab /> : <TextTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={[
        "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
        active
          ? "border-velvet-500 text-velvet-300"
          : "border-transparent text-ink-500 hover:text-white",
      ].join(" ")}
    >
      {icon}
      {children}
    </button>
  );
}

// ============================================================
// Onglet Médias
// ============================================================

function MediaTab(): JSX.Element {
  const { t } = useTranslation();
  const [media, setMedia] = useState<ShareMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [tags, setTags] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() {
    try {
      setError(null);
      setMedia(await listShareMedia());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadShareMedia(file, { label: label || undefined, tags: tags || undefined });
      }
      setLabel("");
      setTags("");
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload échoué");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(m: ShareMedia) {
    if (!window.confirm(t("shareCenter.media.confirmDelete"))) return;
    try {
      await deleteShareMedia(m.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Suppression échouée");
    }
  }

  return (
    <div>
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t("shareCenter.media.labelPlaceholder")}
          className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
        />
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder={t("shareCenter.media.tagsPlaceholder")}
          className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 disabled:opacity-50 text-white text-sm font-medium"
        >
          <Upload className="size-4" />
          {uploading ? t("shareCenter.media.uploading") : t("shareCenter.media.uploadBtn")}
        </button>
        <button
          onClick={() => void refresh()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-sm text-ink-500 hover:text-white"
          title={t("common.refresh")}
        >
          <RefreshCw className="size-4" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <p className="text-ink-500 text-sm mb-3">
        {t(media.length > 1 ? "shareCenter.media.count_plural" : "shareCenter.media.count", { n: media.length })}
      </p>

      {loading && media.length === 0 && <p className="text-ink-500 text-sm">{t("common.loading")}</p>}
      {!loading && media.length === 0 && (
        <div className="text-ink-500 text-sm py-12 text-center border border-dashed border-ink-700 rounded-xl">
          {t("shareCenter.media.empty")}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {media.map((m) => (
          <MediaCard key={m.id} media={m} onDelete={() => void handleDelete(m)} />
        ))}
      </div>
    </div>
  );
}

function MediaCard({ media, onDelete }: { media: ShareMedia; onDelete: () => void }): JSX.Element {
  const { t } = useTranslation();
  const thumb = shareMediaThumbUrl(media.id);
  const [busy, setBusy] = useState<null | "original" | "cleaned">(null);

  async function download(which: "original" | "cleaned") {
    setBusy(which);
    try {
      if (which === "original") await downloadShareMediaOriginal(media);
      else await downloadShareMediaCleaned(media);
    } catch (err) {
      window.alert(`${t("shareCenter.media.downloadError")}: ${err instanceof Error ? err.message : "?"}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl overflow-hidden group">
      <div className="relative aspect-square bg-ink-900 flex items-center justify-center overflow-hidden">
        <img src={thumb} alt={media.label ?? media.filename} className="w-full h-full object-cover" loading="lazy" />
        {media.kind === "video" && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="size-10 rounded-full bg-black/55 flex items-center justify-center">
              <Play className="size-5 text-white" fill="currentColor" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" title={media.label ?? media.filename}>
              {media.label ?? media.filename}
            </p>
            <p className="text-xs text-ink-500">{formatDateTime(media.created_at)}</p>
          </div>
          <button
            onClick={onDelete}
            className="shrink-0 px-2 py-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10"
            title={t("common.delete")}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
        {media.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {media.tags.map((tag) => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-ink-700 text-ink-400">
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => void download("original")}
            disabled={busy !== null}
            className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border border-ink-700 hover:border-ink-600 disabled:opacity-50 text-xs text-ink-300 hover:text-white"
          >
            {busy === "original" ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
            {t("shareCenter.media.downloadOriginal")}
          </button>
          <button
            onClick={() => void download("cleaned")}
            disabled={busy !== null}
            className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-velvet-600 hover:bg-velvet-500 disabled:opacity-50 text-xs text-white font-medium"
            title={t("shareCenter.media.downloadCleaned")}
          >
            {busy === "cleaned" ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
            {busy === "cleaned" ? t("shareCenter.media.cleaning") : t("shareCenter.media.downloadCleaned")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Onglet Textes
// ============================================================

function TextTab(): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<ShareText[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ShareText | "new" | null>(null);

  async function refresh() {
    try {
      setError(null);
      setItems(await listShareText());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleDelete(item: ShareText) {
    if (!window.confirm(t("shareCenter.text.confirmDelete"))) return;
    try {
      await deleteShareText(item.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Suppression échouée");
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-ink-500 text-sm">
          {t(items.length > 1 ? "shareCenter.text.count_plural" : "shareCenter.text.count", { n: items.length })}
        </p>
        <button
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium"
        >
          <Plus className="size-4" />
          {t("shareCenter.text.addBtn")}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && items.length === 0 && <p className="text-ink-500 text-sm">{t("common.loading")}</p>}
      {!loading && items.length === 0 && (
        <div className="text-ink-500 text-sm py-12 text-center border border-dashed border-ink-700 rounded-xl">
          {t("shareCenter.text.empty")}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((item) => (
          <TextCard key={item.id} item={item} onEdit={() => setEditing(item)} onDelete={() => void handleDelete(item)} />
        ))}
      </div>

      {editing && (
        <TextModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function TextCard({
  item,
  onEdit,
  onDelete,
}: {
  item: ShareText;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(item.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold truncate">{item.title}</h3>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="px-2 py-1 rounded text-velvet-400 hover:text-velvet-300 hover:bg-velvet-500/10 text-xs">
            {t("common.edit")}
          </button>
          <button onClick={onDelete} className="px-2 py-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10">
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
      <p className="text-sm text-ink-400 whitespace-pre-wrap line-clamp-4 flex-1">{item.body}</p>
      {item.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.tags.map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-ink-700 text-ink-400">
              {tag}
            </span>
          ))}
        </div>
      )}
      <button
        onClick={() => void copy()}
        className="mt-3 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-ink-700 hover:border-velvet-500 text-sm text-ink-300 hover:text-white transition-colors"
      >
        {copied ? <Check className="size-4 text-emerald-400" /> : <Copy className="size-4" />}
        {copied ? t("shareCenter.text.copied") : t("shareCenter.text.copy")}
      </button>
    </div>
  );
}

function TextModal({
  item,
  onClose,
  onSaved,
}: {
  item: ShareText | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [title, setTitle] = useState(item?.title ?? "");
  const [body, setBody] = useState(item?.body ?? "");
  const [tags, setTags] = useState((item?.tags ?? []).join(", "));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    const tagList = tags.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (item) {
        await updateShareText(item.id, { title, body, tags: tagList });
      } else {
        await createShareText({ title, body, tags: tagList });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec");
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-ink-800 border border-ink-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold mb-4">{item ? t("common.edit") : t("shareCenter.text.addBtn")}</h2>
        {error && <div className="mb-3 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-ink-500 uppercase tracking-wider font-semibold mb-1">
              {t("shareCenter.text.titleLabel")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("shareCenter.text.titlePlaceholder")}
              className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-ink-500 uppercase tracking-wider font-semibold mb-1">
              {t("shareCenter.text.bodyLabel")}
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("shareCenter.text.bodyPlaceholder")}
              rows={6}
              className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm resize-y"
            />
          </div>
          <div>
            <label className="block text-xs text-ink-500 uppercase tracking-wider font-semibold mb-1">
              {t("shareCenter.text.tagsLabel")}
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-sm text-ink-300">
            {t("common.cancel")}
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || !title.trim() || !body.trim()}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 disabled:opacity-50 text-white text-sm font-medium"
          >
            {t("shareCenter.text.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
